import { TIME_CONSTANTS } from '../../constants/time';
import { type createStorageAPI } from '../../utils/typed-storage';

type StorageAPI = ReturnType<typeof createStorageAPI>;

/**
 * Calculates the effective session expiry timestamp using unified logic.
 * Handles re-authentication detection and cross-tab synchronization.
 */
export const calculateEffectiveSessionExpiry = (
	sessionExpiryTimestamp: number | null,
	lastSessionExpiry: number | null,
	previousSessionExpiryTimestamp: number | null = null,
): number | null => {
	// If we only have one value, use it
	if (sessionExpiryTimestamp && !lastSessionExpiry) {
		return sessionExpiryTimestamp;
	} else if (!sessionExpiryTimestamp && lastSessionExpiry) {
		return lastSessionExpiry;
	} else if (sessionExpiryTimestamp && lastSessionExpiry) {
		// Both values available - check for re-authentication detection first
		let useShorterTime = false;
		if (previousSessionExpiryTimestamp) {
			const backward = previousSessionExpiryTimestamp - sessionExpiryTimestamp;
			if (backward > TIME_CONSTANTS.REAUTH_DETECTION_THRESHOLD_MS) {
				// Re-auth detected: use the shorter time
				useShorterTime = true;
			}
		}

		if (useShorterTime) {
			// Re-auth detected: use the new shorter sessionExpiryTimestamp
			return sessionExpiryTimestamp;
		} else {
			// Normal case: use Math.max to handle clock drift and cross-tab updates
			return Math.max(sessionExpiryTimestamp, lastSessionExpiry);
		}
	} else {
		return null;
	}
};

/**
 * Check if banner is currently dismissed and should remain hidden.
 * Returns true if banner was dismissed and dismissal is still valid.
 */
export const isDismissed = (storageAPI: StorageAPI): boolean => {
	const result = storageAPI.sessionBannerDismissed.get();

	if (!result.success) {
		return false;
	}

	const dismissedData = result.data;

	// If hideUntil is not defined or not a number, treat as dismissed indefinitely
	if (typeof dismissedData.hideUntil !== 'number') {
		return true;
	}

	const now = Date.now();

	// Check if hideUntil time has passed
	if (now >= dismissedData.hideUntil) {
		// Remove expired dismissal
		storageAPI.sessionBannerDismissed.remove();
		return false;
	}

	return true;
};

/**
 * Main function to calculate overall banner visibility.
 * Matches the original component logic exactly.
 */
export const calculateBannerVisibility = (
	sessionExpiryTimestamp: number | null,
	lastSessionExpiry: number | null,
	previousSessionExpiryTimestamp: number | null,
	storageAPI: StorageAPI,
): boolean => {
	// Use the extracted function for session expiry calculation
	const effectiveSessionExpiry = calculateEffectiveSessionExpiry(
		sessionExpiryTimestamp,
		lastSessionExpiry,
		previousSessionExpiryTimestamp,
	);

	if (!effectiveSessionExpiry) {
		return false;
	}

	const now = Date.now();
	const timeRemainingMs = effectiveSessionExpiry - now;

	// More than 2 hours: don't show
	if (timeRemainingMs > TIME_CONSTANTS.TWO_HOURS_MS) {
		return false;
	}

	// 15 minutes or less: always show (cannot be dismissed)
	if (timeRemainingMs <= TIME_CONSTANTS.FIFTEEN_MINUTES_MS) {
		return true;
	}

	// Between 15 minutes and 2 hours: respect dismissal state
	const bannerIsDismissed = isDismissed(storageAPI);

	if (bannerIsDismissed) {
		// Was dismissed: hideUntil is set to a future timestamp
		// Banner will automatically show again when hideUntil is reached
		return false;
	}

	// Not dismissed: always show
	return true;
};
